import { lazy, Suspense, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './auth/AuthContext';
import { setTokenGetter } from './api/client';
import LoginPage from './auth/LoginPage';
import AppLayout from './layouts/AppLayout';
import { useCurrentUser } from './hooks/useCurrentUser';

// Lazy-loaded pages
const Dashboard = lazy(() => import('./pages/Dashboard'));
const BillsPage = lazy(() => import('./pages/BillsPage'));
const VendorsPage = lazy(() => import('./pages/VendorsPage'));
const ClePage = lazy(() => import('./pages/ClePage'));
const EmployeesPage = lazy(() => import('./pages/EmployeesPage'));
const SubscriptionsPage = lazy(() => import('./pages/SubscriptionsPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const PayrollHubPage = lazy(() => import('./pages/PayrollHubPage'));
const AdvertisingPage = lazy(() => import('./pages/AdvertisingPage'));
const CallArchivePage = lazy(() => import('./pages/CallArchivePage'));

function PageLoader() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: 'var(--text-muted)' }}>
      Loading...
    </div>
  );
}

function AdminRoute({ children }: { children: ReactNode }) {
  const { data: user } = useCurrentUser();
  if (user && user.role !== 'admin') return <Navigate to="/" replace />;
  return <>{children}</>;
}

function AdminOrAttorneyRoute({ children }: { children: ReactNode }) {
  const { data: user } = useCurrentUser();
  if (user && !['admin', 'attorney'].includes(user.role)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function FinanceRoute({ children }: { children: ReactNode }) {
  const { data: user } = useCurrentUser();
  if (user && !['admin', 'bookkeeper'].includes(user.role)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export default function App() {
  const { isAuthenticated, isLoading, authEnabled, login, getAccessToken } = useAuth();
  const [deactivated, setDeactivated] = useState(false);

  useEffect(() => {
    setTokenGetter(getAccessToken);
  }, [getAccessToken]);

  useEffect(() => {
    const handleUnauthorized = () => login();
    const handleDeactivated = () => setDeactivated(true);
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    window.addEventListener('auth:deactivated', handleDeactivated);
    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
      window.removeEventListener('auth:deactivated', handleDeactivated);
    };
  }, [login]);

  if (isLoading) return <PageLoader />;

  if (deactivated) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'Inter, sans-serif' }}>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ color: 'var(--danger)' }}>Account Deactivated</h2>
          <p style={{ color: 'var(--text-secondary)' }}>Contact an administrator for access.</p>
        </div>
      </div>
    );
  }

  if (authEnabled && !isAuthenticated) return <LoginPage />;

  return (
    <BrowserRouter>
      <Routes>
        <Route element={<AppLayout />}>
          <Route index element={<Suspense fallback={<PageLoader />}><Dashboard /></Suspense>} />
          <Route path="bills" element={<FinanceRoute><Suspense fallback={<PageLoader />}><BillsPage /></Suspense></FinanceRoute>} />
          <Route path="vendors" element={<FinanceRoute><Suspense fallback={<PageLoader />}><VendorsPage /></Suspense></FinanceRoute>} />
          <Route path="cle" element={<Suspense fallback={<PageLoader />}><ClePage /></Suspense>} />
          <Route path="employees" element={<AdminRoute><Suspense fallback={<PageLoader />}><EmployeesPage /></Suspense></AdminRoute>} />
          <Route path="pto" element={<Navigate to="/payroll?tab=time-off" replace />} />
          <Route path="payroll" element={<Suspense fallback={<PageLoader />}><PayrollHubPage /></Suspense>} />
          <Route path="timecards" element={<Navigate to="/payroll?tab=timecards" replace />} />
          <Route path="subscriptions" element={<FinanceRoute><Suspense fallback={<PageLoader />}><SubscriptionsPage /></Suspense></FinanceRoute>} />
          <Route path="advertising" element={<AdminOrAttorneyRoute><Suspense fallback={<PageLoader />}><AdvertisingPage /></Suspense></AdminOrAttorneyRoute>} />
          <Route path="calls" element={<Suspense fallback={<PageLoader />}><CallArchivePage /></Suspense>} />
          <Route path="settings" element={<AdminRoute><Suspense fallback={<PageLoader />}><SettingsPage /></Suspense></AdminRoute>} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
